import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { WifiOff, Wifi, ChevronRight, Zap, MapPin, Navigation } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, Eyebrow, Spinner } from "@/components/tac";

export default function TechToday() {
  const { user } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const { data: jobs, isLoading } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data });
  const { data: mine } = useQuery({ queryKey: ["my-appts"], queryFn: async () => (await api.get("/appointments/mine")).data });

  return (
    <div className="mx-auto max-w-2xl p-5">
      <div className="flex items-center justify-between">
        <div><Eyebrow>Field deck</Eyebrow><h1 className="mt-2 font-display text-3xl font-bold text-white">Today</h1></div>
        <div data-testid="offline-status-badge" className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono-tac text-xs ${online ? "border-emerald-500/40 bg-emerald-950/40 text-emerald-300" : "border-amber-500/40 bg-amber-950/40 text-amber-300"}`}>
          {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          {online ? "Synced" : "Offline - queued"}
        </div>
      </div>
      <p className="mt-1 text-sm text-slate-500">{user?.name} · minimal-tap capture</p>

      {mine?.items?.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="my-schedule">
          <Eyebrow>My route today</Eyebrow>
          {mine.items.map((a) => (
            <div key={a.id} data-testid={`my-appt-${a.id}`} className="card-tac flex items-center justify-between gap-3 p-3">
              <div className="flex items-center gap-3">
                <div data-testid={`route-stop-${a.id}`} className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-500/15 font-mono-tac text-xs font-bold text-amber-300">{a.stop || "·"}</div>
                <div>
                  <div className="text-sm font-semibold text-white">{mine.types[a.type] || a.type} · {a.name}</div>
                  <div className="mt-0.5 flex items-center gap-1 font-mono-tac text-[10px] text-slate-500"><MapPin className="h-3 w-3" />{new Date(a.scheduled_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}{a.address ? ` · ${a.address}` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {a.address && <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a.address)}`} target="_blank" rel="noreferrer" data-testid={`route-directions-${a.id}`} className="rounded-full border border-slate-700 p-1.5 text-cyan-300 hover:border-cyan-400"><Navigation className="h-3.5 w-3.5" /></a>}
                <span className="rounded-full bg-cyan-500/15 px-2 py-0.5 text-[10px] text-cyan-300">{a.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {isLoading ? <div className="mt-10"><Spinner /></div> : (
        <div className="mt-6 space-y-3">
          {jobs.map((j) => (
            <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`tech-job-${j.id}`}
              className="card-tac flex min-h-[72px] items-center justify-between p-4 active:border-amber-500">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-amber-500/10 text-amber-400"><Zap className="h-5 w-5" /></div>
                <div>
                  <div className="text-base font-semibold text-white">{j.title}</div>
                  <div className="mt-0.5 flex items-center gap-1 font-mono-tac text-xs text-slate-500"><MapPin className="h-3 w-3" /> {j.customer_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-2"><StatusPill status={j.status} /><ChevronRight className="h-5 w-5 text-slate-600" /></div>
            </Link>
          ))}
          {!jobs.length && <div className="card-tac py-16 text-center text-slate-500">No jobs on your board today.</div>}
        </div>
      )}
    </div>
  );
}
